import React, { useCallback, useImperativeHandle, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import RoundedModal from '../../../../../molecules/RoundedModal'
import SydButton from '../../../../../commonDesign/Button'
import OperationalTable from '../../../../../organisms/OperationalTable'
import {
  useCreateSupplementalMappingMutation,
  useDeleteSupplementalMappingMutation,
  useListSupplementalMappings,
  useUpdateSupplementalMappingMutation
} from '../../../../../../api/groups'
import { useStatusTemplateContext } from '../StatusTemplateProvider'
import SupplementalSourceForm from './SupplementalSourceForm'

function SupplementalMappingsTableActionsCell ({ row, onEdit, onDelete }) {
  return (
    <div className='flex gap-sm'>
      <SydButton size='sm' variant='ghost' onClick={() => onEdit(row.original)}>Edit</SydButton>
      <SydButton
        size='sm'
        variant='ghost'
        priority='warning'
        onClick={() => onDelete(row.original.supplementalMappingId)}
      >
        Delete
      </SydButton>
    </div>
  )
}

SupplementalMappingsTableActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      supplementalMappingId: PropTypes.number
    }).isRequired
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}

function SupplementalMappingsActionsCellBridge (props) {
  const { row, setFormState, deleteMutation, statusTemplateId } = props
  return (
    <SupplementalMappingsTableActionsCell
      row={row}
      onEdit={(mapping) => setFormState({ open: true, mapping })}
      onDelete={(supplementalMappingId) => {
        if (!window.confirm('Delete this supplemental mapping?')) return
        deleteMutation.mutate({ supplementalMappingId, statusTemplateId })
      }}
    />
  )
}

SupplementalMappingsActionsCellBridge.propTypes = {
  row: SupplementalMappingsTableActionsCell.propTypes.row,
  setFormState: PropTypes.func.isRequired,
  deleteMutation: PropTypes.shape({ mutate: PropTypes.func.isRequired }).isRequired,
  statusTemplateId: PropTypes.number
}

const SupplementalSourcesDialog = React.forwardRef(function SupplementalSourcesDialog (_, ref) {
  const { statusTemplate } = useStatusTemplateContext()
  const [dialogState, setDialogState] = useState({ open: false, templateItem: null })
  const [formState, setFormState] = useState({ open: false, mapping: null })

  const close = useCallback(() => setDialogState({ open: false, templateItem: null }), [])
  const closeForm = useCallback(() => setFormState({ open: false, mapping: null }), [])

  useImperativeHandle(ref, () => ({
    open: (templateItem) => setDialogState({ open: true, templateItem })
  }), [])

  const { data, isLoading } = useListSupplementalMappings(
    statusTemplate?.statusTemplateId,
    dialogState.templateItem?.statusTemplateItemId,
    { enabled: dialogState.open && !!statusTemplate?.statusTemplateId }
  )
  const createMutation = useCreateSupplementalMappingMutation()
  const updateMutation = useUpdateSupplementalMappingMutation()
  const deleteMutation = useDeleteSupplementalMappingMutation()

  const mappings = data?.mappings ?? []
  const definition = data?.definition
  const templateItem = dialogState.templateItem

  const columns = useMemo(() => [
    { Header: 'Order', accessor: 'ordinal' },
    {
      id: 'match',
      Header: 'Match',
      accessor: (row) => {
        if (!row.matchConfig) return '-'
        const { field, op, value } = row.matchConfig
        const display = typeof value === 'string' ? value : JSON.stringify(value)
        return `${field} ${op} ${display}`
      }
    },
    { id: 'enabled', Header: 'Enabled', accessor: (row) => row.enabled ? 'Yes' : 'No' },
    {
      Header: 'Actions',
      id: 'actions',
      Cell: (cellProps) => (
        <SupplementalMappingsActionsCellBridge
          {...cellProps}
          setFormState={setFormState}
          deleteMutation={deleteMutation}
          statusTemplateId={statusTemplate?.statusTemplateId}
        />
      )
    }
  ], [deleteMutation, statusTemplate?.statusTemplateId])

  const handleFormComplete = useCallback(async (payload) => {
    try {
      if (payload.supplementalMappingId) {
        await updateMutation.mutateAsync({ statusTemplateId: statusTemplate?.statusTemplateId, ...payload })
      } else {
        await createMutation.mutateAsync({
          statusTemplateId: statusTemplate?.statusTemplateId,
          statusTemplateItemId: templateItem?.statusTemplateItemId,
          ...payload
        })
      }
      closeForm()
    } catch (err) {
      console.error('Failed to save supplemental mapping', err)
    }
  }, [statusTemplate, templateItem, createMutation, updateMutation, closeForm])

  return (
    <>
      <RoundedModal
        title={`Supplemental mappings ${templateItem ? `- ${templateItem.displayName}` : ''}`}
        open={dialogState.open}
        onClose={close}
        position='top'
        disablePortal={false}
      >
        <div className='flex flex-col gap-md'>
          <p className='font-size-sm'>
            Configure how this item maps into the template&apos;s supplemental source (match rules and display). Set the source type and code on the Supplemental Data tab.
          </p>
          {definition?.sourceKey
            ? (
              <p className='font-size-sm text-secondary'>
                Template supplemental source: <span className='font-mono'>{definition.sourceKey}</span> ({definition.sourceType})
              </p>
            )
            : (
              <p className='font-size-sm' style={{ color: 'var(--color-warning-500)' }}>
                No template supplemental source configured yet. Choose a source type and code on the Supplemental Data tab before supplemental data will load on reports.
              </p>
            )}
          {templateItem && (
            <>
              <SydButton
                size='sm'
                variant='primary'
                icon='add'
                onClick={() => setFormState({ open: true, mapping: null })}
              >
                Add mapping
              </SydButton>
              <OperationalTable
                variant='v2'
                mode='client'
                itemName='Mappings'
                data={mappings}
                loading={isLoading}
                columns={columns}
                defaultSort={[]}
              />
            </>
          )}
        </div>
      </RoundedModal>

      {formState.open && (
        <RoundedModal
          title={formState.mapping ? 'Edit supplemental mapping' : 'Add supplemental mapping'}
          open
          onClose={closeForm}
          position='top'
          disablePortal={false}
        >
          <SupplementalSourceForm
            templateItem={templateItem}
            mapping={formState.mapping}
            onCancel={closeForm}
            onComplete={handleFormComplete}
          />
        </RoundedModal>
      )}
    </>
  )
})

export default SupplementalSourcesDialog
